// src/components/common/LiteYouTube.tsx
//
// YouTube facade: a poster + play button until the visitor asks for the
// video. The real player (≈1 MB of script, several requests) only loads on
// click. Cuts the home page's initial payload and keeps the hero's LCP on
// our own image instead of YouTube's iframe.

import React, { useState } from 'react';
import { Box, ButtonBase } from '@mui/material';
import { PlayArrowRounded as PlayIcon } from '@mui/icons-material';
import clarityService from '../../services/clarityService';

interface LiteYouTubeProps {
  videoId: string;
  title: string;
  /** Optional poster; defaults to YouTube's own thumbnail. */
  poster?: string;
}

const LiteYouTube: React.FC<LiteYouTubeProps> = ({ videoId, title, poster }) => {
  const [playing, setPlaying] = useState(false);
  const thumb = poster || `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`;

  return (
    <Box
      sx={{
        position: 'relative',
        paddingTop: '56.25%',
        borderRadius: 2,
        overflow: 'hidden',
        boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
        bgcolor: '#000',
      }}
    >
      {playing ? (
        <Box
          component="iframe"
          src={`https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1&rel=0&modestbranding=1`}
          title={title}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 'none' }}
        />
      ) : (
        <ButtonBase
          onClick={() => {
            clarityService.track('cta_click', 'watch_demo');
            setPlaying(true);
          }}
          aria-label={`Play video: ${title}`}
          sx={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            display: 'block',
            '&:hover .play, &:focus-visible .play': { transform: 'translate(-50%, -50%) scale(1.08)' },
          }}
        >
          <Box
            component="img"
            src={thumb}
            alt=""
            loading="lazy"
            decoding="async"
            sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
          />
          <Box
            className="play"
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: 72,
              height: 72,
              borderRadius: '50%',
              bgcolor: 'rgba(255,255,255,0.92)',
              color: '#111',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'transform 0.2s',
              boxShadow: '0 8px 24px rgba(0,0,0,0.35)',
            }}
          >
            <PlayIcon sx={{ fontSize: 44, ml: 0.5 }} />
          </Box>
          <Box
            sx={{
              position: 'absolute',
              left: 12,
              bottom: 10,
              px: 1.2,
              py: 0.4,
              borderRadius: 1,
              bgcolor: 'rgba(0,0,0,0.6)',
              color: 'white',
              fontSize: '0.8rem',
              fontWeight: 600,
            }}
          >
            Watch the 2-minute demo
          </Box>
        </ButtonBase>
      )}
    </Box>
  );
};

export default LiteYouTube;
